import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { RadioListStyle, SelectItem } from '@ftr/forms'
import { DestroySubscribers } from '@ftr/foundation'
import { Observable, of, takeUntil, tap } from 'rxjs'
import { ComplexFilterData, ComplexFilterValue } from '../ComplexFilter'
import { RadioListFilter } from '../RadioListFilter'
import { ComplexFilterService } from '../complex-filter.service'

@Component({
  selector: 'ftr-complex-filter-radio-list',
  templateUrl: './complex-filter-radio-list.component.html',
  styleUrls: ['./complex-filter-radio-list.component.css'],
})
export class ComplexFilterRadioListComponent extends DestroySubscribers implements OnInit {
  /**
   * Filter configuration, must be specific to the radio-list filter type
   */
  @Input() filter: RadioListFilter
  @Input() visibilityToggled: Observable<boolean>
  @Output() filterChanged = new EventEmitter<ComplexFilterData>()

  readonly radioListStyle = RadioListStyle
  internalFormGroup: UntypedFormGroup = new UntypedFormGroup({})
  options: SelectItem<string>[][] = []
  hiddenOptions: SelectItem<string>[][] = []
  private lastValue: ComplexFilterData

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly complexFilterService: ComplexFilterService,
  ) {
    super()
  }

  ngOnInit(): void {
    if (!this.filter.options || !this.filter.options.length) {
      throw new Error('No options provided for the radio list filter')
    }

    this.buildInternalFormControls()
    this.listenToFilterChange()
    this.listenToVisibilityChange()
  }

  getRadioListOptions(index: number): Observable<SelectItem<string>[]> {
    return of(this.options[index])
  }

  listenToFilterChange(): void {
    this.complexFilterService
      .listenToFilterChange([this.filter])
      .pipe(takeUntil(this.finalize))
      .subscribe(filters => {
        this.checkForHiddenOptions(filters[this.filter.filterKey])
        // Patch the form without emitting
        this.internalFormGroup.patchValue(filters, { emitEvent: false })
        this.lastValue = this.internalFormGroup.value
      })
  }

  /**
   * This is to ensure the value of the filter resets to the previous one
   * when we change it manually and close the modal without applying.
   * Still works when applied immediately, because lastValue is updated.
   */
  private listenToVisibilityChange(): void {
    this.visibilityToggled
      .pipe(
        takeUntil(this.finalize),
        tap(visible => {
          if (!visible) {
            this.internalFormGroup.reset(this.lastValue, { emitEvent: false })
          }
        }),
      )
      .subscribe()
  }

  private buildInternalFormControls(): void {
    ;[this.filter].concat(this.filter.additionalFilters || []).forEach(filter => {
      this.options.push([...filter.options])
      this.hiddenOptions.push([...(filter.hiddenOptions || [])])
    })

    if (!this.filter.additionalFilters || !this.filter.additionalFilters.length) {
      // If it's a single filter, create a form control
      this.internalFormGroup.addControl(this.filter.filterKey, new UntypedFormControl())
    } else {
      // Create an array with a form control for each additional filter
      this.internalFormGroup.addControl(
        this.filter.filterKey,
        this.formBuilder.array(
          [this.filter].concat(this.filter.additionalFilters || []).map(() => new UntypedFormControl()),
        ),
      )
    }

    this.internalFormGroup.valueChanges.pipe(takeUntil(this.finalize)).subscribe(formValue => {
      this.filterChanged.emit(formValue)
    })
  }

  /**
   * If the filter value is not found in filter options, it may be a hidden option.
   * If it is, add it to the array of options
   */
  private checkForHiddenOptions(value: ComplexFilterValue): void {
    const checkSingleOption = (v: ComplexFilterValue, i: number): void => {
      const option = this.options[i].find(o => o.value === v)
      const hiddenOption = this.hiddenOptions[i].find(h => h.value === v)
      if (!option && hiddenOption) {
        this.options[i].push(hiddenOption)
      }
    }

    if (Array.isArray(value) && value.length) {
      value.forEach((v, i) => checkSingleOption(v, i))
    } else {
      checkSingleOption(value, 0)
    }
  }

  get filterControlAsFormControl(): UntypedFormControl {
    return this.internalFormGroup.controls[this.filter.filterKey] as UntypedFormControl
  }

  get filterControlChildren(): UntypedFormControl[] {
    const formArray = this.internalFormGroup.controls[this.filter.filterKey] as UntypedFormArray
    return formArray.controls.map(x => x as UntypedFormControl)
  }
}
